// Thin fetch wrapper. Every non-2xx response becomes an ApiClientError with the server's
// Slovenian message; network failures become ApiClientError with status 0.
//
// A 2xx answer only counts when it really comes from our API: a followed redirect or a non-JSON
// body (an SSO / captive / filtering proxy page) becomes a transient ApiClientError (status 0), so
// the outbox retries instead of deleting an op the server never saw.
//
// A 401 `unauthorized` answer means the session ended on the server (revoked, expired, user
// disabled): the handler set with setUnauthorizedHandler is told (api.ts shows the login), unless
// a sign-in happened while the request was under way (see bumpAuthEpoch).
import type { ApiError } from '../../../shared/types';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
  }

  /** Network failure, timeout or server error – worth retrying later. */
  get transient(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

export interface RequestOptions {
  method?: string;
  /** JSON-serialised unless it is FormData/Blob. */
  body?: unknown;
  signal?: AbortSignal;
  /** Milliseconds; default 30 s, uploads should pass a larger value. */
  timeoutMs?: number;
}

const MSG_OFFLINE = 'Ni povezave s strežnikom.';
const MSG_TIMEOUT = 'Strežnik se ni odzval pravočasno.';
const MSG_UNEXPECTED = 'Strežnik je vrnil nepričakovan odgovor.';

// ---------- session ended on the server ----------

let epoch = 0;
let unauthorizedHandler: (() => void) | null = null;
/** Answers 401 here when there is no session to end: not a sign of an ended session. */
const LOGOUT_URLS = new Set(['/api/auth/logout', '/api/logout']);

/** Counts sign-ins in this tab: a 401 to a request sent before the latest one is stale. */
export function authEpoch(): number {
  return epoch;
}

/** A new session was established (login / setup): answers to older requests say nothing about it. */
export function bumpAuthEpoch(): void {
  epoch++;
}

/** Called once per 401 `unauthorized` answer (any request, except the logout itself). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

function noteAnswer(url: string, sentAt: number, err: unknown): void {
  if (!(err instanceof ApiClientError) || err.status !== 401 || err.code !== 'unauthorized') return;
  if (sentAt !== epoch || LOGOUT_URLS.has(url.split('?')[0]!)) return;
  try {
    unauthorizedHandler?.();
  } catch (handlerError) {
    console.error('[http] unauthorized handler failed', handlerError);
  }
}

// ---------- AbortSignal helpers (AbortSignal.any: Safari 17.4+, Chrome 116+; .timeout: Safari 16+) ----------

interface ManagedSignal {
  signal: AbortSignal;
  /** Stop timers / listeners once the request is over. */
  dispose: () => void;
}

function timeoutSignal(ms: number): ManagedSignal {
  if (typeof AbortSignal.timeout === 'function') return { signal: AbortSignal.timeout(ms), dispose: () => {} };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('Signal timed out.', 'TimeoutError')), ms);
  return { signal: controller.signal, dispose: () => clearTimeout(timer) };
}

function anySignal(signals: AbortSignal[]): ManagedSignal {
  if (typeof AbortSignal.any === 'function') return { signal: AbortSignal.any(signals), dispose: () => {} };
  const controller = new AbortController();
  const onAbort = (event: Event) => controller.abort((event.target as AbortSignal).reason);
  for (const s of signals) {
    if (s.aborted) {
      controller.abort(s.reason);
      break;
    }
    s.addEventListener('abort', onAbort, { once: true });
  }
  return {
    signal: controller.signal,
    dispose: () => {
      for (const s of signals) s.removeEventListener('abort', onAbort);
    },
  };
}

// ---------- responses ----------

interface RawResponse {
  status: number;
  text: string;
  contentType: string | null;
  redirected: boolean;
}

function isJsonType(contentType: string | null): boolean {
  const base = (contentType ?? '').split(';')[0]!.trim().toLowerCase();
  return base === 'application/json' || base.endsWith('+json');
}

function parseResponse<T>(res: RawResponse): T {
  const ok = res.status >= 200 && res.status < 300;
  // fetch follows redirects silently; our API never redirects, so this is someone else's page.
  if (ok && res.redirected) throw new ApiClientError(0, 'redirected', MSG_UNEXPECTED);
  if (res.status === 204) return undefined as T;
  let json: unknown = undefined;
  let parsed = false;
  if (res.text) {
    try {
      json = JSON.parse(res.text);
      parsed = true;
    } catch {
      json = undefined;
    }
  }
  if (!ok) {
    const e = (json ?? {}) as Partial<ApiError>;
    throw new ApiClientError(res.status, e.error ?? `http_${res.status}`, e.message ?? `Napaka strežnika (${res.status}).`);
  }
  if (res.text && (!parsed || !isJsonType(res.contentType))) throw new ApiClientError(0, 'bad_response', MSG_UNEXPECTED);
  return json as T;
}

// ---------- fetch ----------

export async function request<T>(url: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, timeoutMs = 30_000 } = opts;
  const headers: Record<string, string> = {};
  let payload: BodyInit | undefined;
  if (body instanceof FormData || body instanceof Blob) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const timeout = timeoutSignal(timeoutMs);
  const combined = signal ? anySignal([signal, timeout.signal]) : null;
  const networkError = (err: unknown): unknown => {
    if (signal?.aborted) return err;
    return new ApiClientError(0, 'network', timeout.signal.aborted ? MSG_TIMEOUT : MSG_OFFLINE);
  };
  const sentAt = epoch;

  try {
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers,
        body: payload,
        signal: combined?.signal ?? timeout.signal,
        credentials: 'same-origin',
      });
    } catch (err) {
      throw networkError(err);
    }
    let text = '';
    if (res.status !== 204) {
      try {
        text = await res.text();
      } catch (err) {
        // The connection dropped (or timed out) while the body was being read.
        throw networkError(err);
      }
    }
    try {
      return parseResponse<T>({
        status: res.status,
        text,
        contentType: res.headers.get('content-type'),
        redirected: res.redirected === true,
      });
    } catch (err) {
      noteAnswer(url, sentAt, err);
      throw err;
    }
  } finally {
    timeout.dispose();
    combined?.dispose();
  }
}

// ---------- uploads ----------

export interface UploadOptions {
  method?: string;
  /** Give up when the request body made no progress for this long (default 60 s). */
  stallTimeoutMs?: number;
  /**
   * How long the server may take to answer once the whole body is sent (default 10 min: it
   * remuxes and probes the audio first; the reverse proxy allows 15 min).
   */
  responseTimeoutMs?: number;
  /** Total timeout when XMLHttpRequest is not available and fetch is used instead. */
  fallbackTimeoutMs?: number;
}

const UPLOAD_STALL_MS = 60_000;
const UPLOAD_RESPONSE_MS = 10 * 60_000;

function isRedirected(url: string, responseUrl: string): boolean {
  if (!responseUrl) return false;
  try {
    return new URL(url, location.href).href !== responseUrl;
  } catch {
    return false;
  }
}

/**
 * Send a large body (audio upload). Uses XMLHttpRequest for its upload progress events: the
 * request is only abandoned when it stops making progress, so a long recording also gets through
 * a slow mobile uplink instead of timing out and starting from zero again and again.
 */
export function upload<T>(url: string, body: FormData | Blob, opts: UploadOptions = {}): Promise<T> {
  const { method = 'PUT', stallTimeoutMs = UPLOAD_STALL_MS, responseTimeoutMs = UPLOAD_RESPONSE_MS } = opts;
  if (typeof XMLHttpRequest === 'undefined') {
    return request<T>(url, { method, body, timeoutMs: opts.fallbackTimeoutMs ?? responseTimeoutMs });
  }
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };
    const fail = (message: string) => settle(() => reject(new ApiClientError(0, 'network', message)));
    const arm = (ms: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fail(MSG_TIMEOUT);
        try {
          xhr.abort();
        } catch {
          // Already done.
        }
      }, ms);
    };

    try {
      xhr.open(method, url, true);
      xhr.responseType = 'text';
    } catch {
      fail(MSG_OFFLINE);
      return;
    }
    const progress = xhr.upload as XMLHttpRequestUpload | undefined;
    if (progress) {
      progress.onprogress = () => arm(stallTimeoutMs);
      // The body is out: from now on only the server's processing time counts.
      progress.onload = () => arm(responseTimeoutMs);
    }
    xhr.onload = () =>
      settle(() => {
        if (xhr.status === 0) {
          reject(new ApiClientError(0, 'network', MSG_OFFLINE));
          return;
        }
        try {
          resolve(
            parseResponse<T>({
              status: xhr.status,
              text: typeof xhr.responseText === 'string' ? xhr.responseText : '',
              contentType: xhr.getResponseHeader('content-type'),
              redirected: isRedirected(url, xhr.responseURL),
            }),
          );
        } catch (err) {
          reject(err);
        }
      });
    xhr.onerror = () => fail(MSG_OFFLINE);
    xhr.onabort = () => fail(MSG_OFFLINE);
    xhr.ontimeout = () => fail(MSG_TIMEOUT);
    // Without upload events only a total limit is possible.
    arm(progress ? stallTimeoutMs : stallTimeoutMs + responseTimeoutMs);
    try {
      xhr.send(body);
    } catch {
      fail(MSG_OFFLINE);
    }
  });
}
